import { pick } from '../pick';
import { unwrapApiItems, unwrapListItems } from '../../api/unwrap';

// isActive is the per-procurement switch; data without it falls back to
// the master clause's isShow.
export const isProcurementClauseActive = clause =>
  clause?.isActive != null ? clause.isActive === true : clause?.isShow === true;

export const getVisibleProcurementClauses = (procurement = {}) =>
  [...((procurement ?? {}).procurementClauseDetails ?? [])]
    .filter(isProcurementClauseActive)
    .sort((a, b) => (a.ordreNumber ?? 0) - (b.ordreNumber ?? 0));

export const hasClauseResponse = value => Boolean(String(value ?? '').trim());

export const toLockedFlag = value =>
  value === true || value === 1 || value === '1' || value === 'true';

export const expectIsPertaminaFromView = isEvaluatorView => !isEvaluatorView;

export const isUnreadForRole = (item, expectIsPertamina) =>
  Boolean(item?.id) &&
  item.isRead !== true &&
  !isAgreementRow(item) &&
  toPertaminaFlag(item.isPertamina) === expectIsPertamina;

export const matchesRespondMitra = (
  item,
  procurementMitraId,
  { allowUnscoped = false } = {}
) => {
  if (!procurementMitraId) return true;
  if (item?.procurementMitraId == null) return allowUnscoped === true;
  return String(item.procurementMitraId) === String(procurementMitraId);
};

export const unwrapRespondListItems = payload => {
  const fromApi = unwrapApiItems(payload);
  if (fromApi.length > 0) return fromApi;
  return unwrapListItems(payload);
};

export const filterRespondItemsByMitra = (
  items,
  procurementMitraId,
  { scopedQuery = false } = {}
) => {
  const list = items ?? [];
  if (!procurementMitraId || scopedQuery) return list;
  return list.filter(item =>
    matchesRespondMitra(item, procurementMitraId, { allowUnscoped: true })
  );
};

export const hasUnreadRespondsForRole = (items, isEvaluatorView = true) => {
  const expectIsPertamina = expectIsPertaminaFromView(isEvaluatorView);
  return (items ?? []).some(item => isUnreadForRole(item, expectIsPertamina));
};

export const normalizeRespondClauseDetail = raw => {
  if (!raw || typeof raw !== 'object') return null;

  const procurementClauseDetailId = pick(
    raw,
    'procurementClauseDetailId',
    'ProcurementClauseDetailId'
  );

  if (!procurementClauseDetailId) return null;

  return {
    id: pick(raw, 'id', 'Id'),
    procurementMitraId: pick(raw, 'procurementMitraId', 'ProcurementMitraId'),
    procurementClauseDetailId,
    respondNotes: pick(raw, 'respondNotes', 'RespondNotes') ?? '',
    isConfirmed: pick(raw, 'isConfirmed', 'IsConfirmed') === true,
    isPertamina: pick(raw, 'isPertamina', 'IsPertamina'),
    createdByLabel: pick(raw, 'createdByLabel', 'CreatedByLabel') ?? '',
    createdDate: pick(raw, 'createdDate', 'CreatedDate'),
    updatedDate: pick(raw, 'updatedDate', 'UpdatedDate'),
    isLocked: toLockedFlag(pick(raw, 'isLocked', 'IsLocked')),
    isRead: pick(raw, 'isRead', 'IsRead') === true,
    finalValue: pick(raw, 'finalValue', 'FinalValue'),
  };
};

export const TYPE_RESPONSE_TOLERANCE = 1;
export const TYPE_RESPONSE_VALIDITY = 2;
export const TYPE_RESPONSE_DOCUMENT = 3;
export const TYPE_RESPONSE_GLOBAL = 4;
export const TYPE_RESPONSE_COUNTER_PAYMENT_TERM = 5;
export const TYPE_RESPONSE_COUNTER_COMPLIANCE = 6;
export const TYPE_RESPONSE_PRODUCT_SPECIFICATION = 7;
// Per-field Payment Terms (8-11) / Compliance (12-15) threads; 5/6 = legacy.
export const TYPE_RESPONSE_INVOICING_QUANTITY = 8;
export const TYPE_RESPONSE_PAYMENT_MODE = 9;
export const TYPE_RESPONSE_PAYMENT_CURRENCY = 10;
export const TYPE_RESPONSE_DESCRIPTION_OF_PAYMENT_TERM = 11;
export const TYPE_RESPONSE_LAW = 12;
export const TYPE_RESPONSE_ARBITRATION = 13;
export const TYPE_RESPONSE_OTHER_TERMS_AND_CONDITION = 14;
export const TYPE_RESPONSE_ADDITIONAL_NOTES = 15;
// Agree per field Operational Tolerance / Validity (bukan thread).
export const TYPE_RESPONSE_OT_VALUE = 16;
export const TYPE_RESPONSE_OT_OPTIONS = 17;
export const TYPE_RESPONSE_VALIDITY_VALID_TO = 18;
// Agree per field free-text (Final HTML, tidak di-encode); 11/14/15 = thread.
export const TYPE_RESPONSE_DESCRIPTION_OF_PAYMENT_TERM_AGREED = 19;
export const TYPE_RESPONSE_OTHER_TERMS_AND_CONDITION_AGREED = 20;
export const TYPE_RESPONSE_ADDITIONAL_NOTES_AGREED = 21;
// Baris agreement per field (PUT agree-field): bukan thread → tanpa unread.
export const AGREEMENT_TYPE_RESPONSES = [
  8, 9, 10, 12, 13, 16, 17, 18, 19, 20, 21,
];
const isAgreementRow = item =>
  AGREEMENT_TYPE_RESPONSES.includes(
    Number(item?.typeResponse ?? item?.TypeResponse)
  );

export const normalizeRespondOperationalToleranceValidity = raw => {
  if (!raw || typeof raw !== 'object') return null;

  const procurementId = pick(raw, 'procurementId', 'ProcurementId');
  const typeResponse = Number(pick(raw, 'typeResponse', 'TypeResponse'));

  if (!procurementId || !Number.isFinite(typeResponse)) return null;

  return {
    id: pick(raw, 'id', 'Id'),
    procurementId,
    procurementMitraId: pick(raw, 'procurementMitraId', 'ProcurementMitraId'),
    respondNotes: pick(raw, 'respondNotes', 'RespondNotes') ?? '',
    typeResponse,
    isPertamina: pick(raw, 'isPertamina', 'IsPertamina'),
    createdByLabel: pick(raw, 'createdByLabel', 'CreatedByLabel') ?? '',
    createdDate: pick(raw, 'createdDate', 'CreatedDate'),
    updatedDate: pick(raw, 'updatedDate', 'UpdatedDate'),
    isResponded: pick(raw, 'isResponded', 'IsResponded') === true,
    isLocked: toLockedFlag(pick(raw, 'isLocked', 'IsLocked')),
    isRead: pick(raw, 'isRead', 'IsRead') === true,
    finalValue: pick(raw, 'finalValue', 'FinalValue'),
  };
};

export const getResponseSortTime = item => {
  const updated = item?.updatedDate
    ? new Date(item.updatedDate).getTime()
    : NaN;
  const created = item?.createdDate
    ? new Date(item.createdDate).getTime()
    : NaN;
  if (!Number.isNaN(updated)) return updated;
  if (!Number.isNaN(created)) return created;
  return Number.POSITIVE_INFINITY;
};

export const stampSavedResponse = (saved, { isEvaluator = false } = {}) => {
  if (!saved) return null;
  const now = new Date().toISOString();
  const hasFlag = saved.isPertamina === true || saved.isPertamina === false;
  return {
    ...saved,
    isPertamina: hasFlag ? saved.isPertamina : Boolean(isEvaluator),
    createdDate: saved.createdDate ?? now,
    updatedDate: saved.updatedDate ?? now,
  };
};

export const sortRespondOperationalToleranceValidities = items =>
  [...(items ?? [])].sort(
    (a, b) => getResponseSortTime(a) - getResponseSortTime(b)
  );

export const mapResponsesByTypeResponse = items => {
  const acc = {
    [TYPE_RESPONSE_TOLERANCE]: [],
    [TYPE_RESPONSE_VALIDITY]: [],
    [TYPE_RESPONSE_DOCUMENT]: [],
    [TYPE_RESPONSE_GLOBAL]: [],
    [TYPE_RESPONSE_COUNTER_PAYMENT_TERM]: [],
    [TYPE_RESPONSE_COUNTER_COMPLIANCE]: [],
    [TYPE_RESPONSE_PRODUCT_SPECIFICATION]: [],
  };

  (items ?? []).forEach(item => {
    if (!item || item.typeResponse == null) return;
    const key = Number(item.typeResponse);
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
  });

  Object.keys(acc).forEach(key => {
    acc[key] = sortRespondOperationalToleranceValidities(acc[key]);
  });

  return acc;
};

export const toClauseResponseList = value => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return [value];
  return [];
};

export const toToleranceValidityHistoryItems = (responses = []) =>
  toClauseResponseList(responses)
    .filter(item => hasClauseResponse(item?.respondNotes))
    .map(item => ({
      ...item,
      display: item.respondNotes,
    }));

export const sortRespondClauseDetails = items =>
  [...(items ?? [])].sort(
    (a, b) => getResponseSortTime(a) - getResponseSortTime(b)
  );

export const mapResponsesByClauseId = items => {
  const acc = {};

  (items ?? []).forEach(item => {
    if (!item?.procurementClauseDetailId) return;
    const key = item.procurementClauseDetailId;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
  });

  Object.keys(acc).forEach(key => {
    acc[key] = sortRespondClauseDetails(acc[key]);
  });

  return acc;
};

export const toPertaminaFlag = value =>
  value === true || value === 1 || value === '1' || value === 'true';

export const isEvaluatorResponse = response =>
  toPertaminaFlag(response?.isPertamina);

export const getOwnClauseResponse = (
  responses,
  { isEvaluator = false } = {}
) => {
  const list = toClauseResponseList(responses);
  if (!list.length) return null;

  if (isEvaluator) {
    return [...list].reverse().find(item => isEvaluatorResponse(item)) ?? null;
  }

  return [...list].reverse().find(item => !isEvaluatorResponse(item)) ?? null;
};

export const isBidderClauseConfirmed = responses => {
  const own = getOwnClauseResponse(responses, { isEvaluator: false });
  if (!own) return false;
  return own.isConfirmed === true || hasClauseResponse(own.respondNotes);
};

export const buildConfirmedClauseIds = (clauseResponses = {}) => {
  const next = {};
  Object.entries(clauseResponses).forEach(([clauseId, responses]) => {
    if (isBidderClauseConfirmed(responses)) {
      next[clauseId] = true;
    }
  });
  return next;
};

export const mapOwnResponsesByClauseId = (
  items,
  { isEvaluator = false } = {}
) => {
  const grouped = mapResponsesByClauseId(items);
  return Object.keys(grouped).reduce((acc, key) => {
    acc[key] = getOwnClauseResponse(grouped[key], { isEvaluator });
    return acc;
  }, {});
};

export const isLastResponseEvaluator = responses => {
  const list = sortRespondClauseDetails(
    toClauseResponseList(responses).filter(item =>
      hasClauseResponse(item?.respondNotes)
    )
  );
  if (!list.length) return false;
  return isEvaluatorResponse(list[list.length - 1]);
};

export const needsIcReplyOnEvaluatorThread = responses => {
  const list = sortRespondClauseDetails(
    toClauseResponseList(responses).filter(item =>
      hasClauseResponse(item?.respondNotes)
    )
  );
  if (!list.length) return false;
  const last = list[list.length - 1];
  if (toLockedFlag(last?.isLocked)) return false;
  return isEvaluatorResponse(last);
};

export const getLastOwnEditableResponse = (
  responses,
  { isEvaluator = false } = {}
) => {
  const list = sortRespondClauseDetails(
    toClauseResponseList(responses).filter(item =>
      hasClauseResponse(item?.respondNotes)
    )
  );
  if (!list.length) return null;
  if (!isEvaluator && !list.some(isEvaluatorResponse)) return null;
  const last = list[list.length - 1];
  if (toLockedFlag(last?.isLocked)) return null;
  if (isEvaluatorResponse(last) !== isEvaluator) return null;
  return last;
};

export const canAgreeLastCounter = (responses, item) => {
  if (!item) return false;
  const list = sortRespondClauseDetails(
    toClauseResponseList(responses).filter(entry =>
      hasClauseResponse(entry?.respondNotes)
    )
  );
  if (!list.length) return false;
  const last = list[list.length - 1];
  if (isEvaluatorResponse(last)) return false;
  return String(last.id) === String(item.id);
};

export const needsTeAgreeOnLastCounter = responses => {
  const list = sortRespondClauseDetails(
    toClauseResponseList(responses).filter(item =>
      hasClauseResponse(item?.respondNotes)
    )
  );
  if (!list.length) return false;
  const last = list[list.length - 1];
  if (toLockedFlag(last?.isLocked)) return false;
  return !isEvaluatorResponse(last);
};

/** TE Send Clarification: last message must be unlocked evaluator respond. */
export const hasTeClarificationRespondReady = responses => {
  const list = sortRespondClauseDetails(
    toClauseResponseList(responses).filter(item =>
      hasClauseResponse(item?.respondNotes)
    )
  );
  if (!list.length) return false;
  const last = list[list.length - 1];
  if (toLockedFlag(last?.isLocked)) return false;
  return isEvaluatorResponse(last);
};
